/* ============================================================
   CART DRAWER - Carrito desplegable lateral
   Funcionalidad AJAX completa sobre la Shopify Cart API.

   Toda la lógica compartida (fetch, formato de moneda, escape, tamaño de
   imagen, umbral de envío, anuncios para lectores de pantalla) vive en
   assets/cart-utils.js. theme.liquid lo carga en <head> con defer, así que
   window.AeroCartUtils siempre existe antes de que corra este archivo.
   ============================================================ */

(function () {
  "use strict";

  var utils = window.AeroCartUtils;
  if (!utils) {
    console.error("cart-drawer.js: AeroCartUtils no está cargado.");
    return;
  }

  /* ---------- CONFIGURACIÓN ---------- */
  var CONFIG = {
    AUTO_OPEN_ON_ADD: true,
    SHOW_SHIPPING_BAR: true,
    HIGHLIGHT_KEYWORD: "AERO",
    DEBOUNCE_DELAY: utils.CONFIG.DEBOUNCE_DELAY,
  };

  /* Textos inyectados por snippets/cart-drawer.liquid desde locales/. */
  var STRINGS = (function () {
    var node = document.getElementById("cartDrawerStrings");
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      console.error("cart-drawer.js: no se pudieron leer los textos", e);
      return {};
    }
  })();

  function t(key, replacements) {
    var value = STRINGS[key];
    if (typeof value !== "string") return "";
    if (!replacements) return value;
    Object.keys(replacements).forEach(function (token) {
      value = value.split(token).join(replacements[token]);
    });
    return value;
  }

  /* ---------- CLASE PRINCIPAL ---------- */
  class CartDrawer {
    constructor() {
      // Elementos del DOM
      this.drawer = document.getElementById("cartDrawer");
      this.backdrop = document.getElementById("cartDrawerBackdrop");
      this.closeBtn = document.getElementById("cartDrawerClose");
      this.body = document.getElementById("cartDrawerBody");
      this.itemsContainer = document.getElementById("cartDrawerItems");
      this.emptyState = document.getElementById("cartDrawerEmpty");
      this.footer = document.getElementById("cartDrawerFooter");
      this.subtotalEl = document.getElementById("cartDrawerSubtotal");
      this.shippingBar = document.getElementById("cartShippingBar");
      this.shippingProgress = document.getElementById("cartShippingProgress");
      this.shippingMsg = document.getElementById("cartShippingMsg");
      this.emptyCta = document.getElementById("cartDrawerEmptyCta");

      // Estado interno
      this.isOpen = false;
      this.isUpdating = false;
      this.cart = null;
      this.lastFocusedElement = null;

      // Inicializar
      if (this.drawer) {
        this.init();
      }
    }

    /* ===== INICIALIZACIÓN ===== */
    init() {
      this.setInert(true);
      this.bindEvents();
      this.refreshCart(false); // Cargar carrito sin abrir drawer
    }

    /* ===== EVENTOS ===== */
    bindEvents() {
      // Abrir drawer — botón del header
      var cartBtns = document.querySelectorAll(
        "#cart-icon-btn, .aero-navbar__cart-btn, [data-open-cart-drawer]",
      );
      cartBtns.forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Cerrar drawer
      if (this.closeBtn) {
        this.closeBtn.addEventListener("click", () => this.close());
      }
      if (this.backdrop) {
        this.backdrop.addEventListener("click", () => this.close());
      }

      // Tecla ESC
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.isOpen) {
          this.close();
        }
      });

      // Cerrar drawer al hacer click en CTA vacío
      if (this.emptyCta) {
        this.emptyCta.addEventListener("click", () => {
          this.close();
        });
      }

      // Delegación de eventos en items (eliminar + cantidad +/-)
      if (this.itemsContainer) {
        this.itemsContainer.addEventListener("click", (e) => {
          // Botón eliminar
          var removeBtn = e.target.closest(".cart-drawer__item-remove");
          if (removeBtn) {
            var removeKey = removeBtn.dataset.key;
            if (removeKey) this.removeItem(removeKey);
            return;
          }

          // Botones +/-
          var qtyBtn = e.target.closest(".cart-drawer__qty-btn");
          if (qtyBtn) {
            var key = qtyBtn.dataset.key;
            var currentQty = parseInt(qtyBtn.dataset.qty, 10);
            var action = qtyBtn.dataset.action;
            if (action === "increase" && currentQty < 99) {
              this.updateQuantity(key, currentQty + 1);
            } else if (action === "decrease" && currentQty > 1) {
              this.updateQuantity(key, currentQty - 1);
            }
          }
        });

        // Input manual de cantidad con debounce
        var qtyDebounce;
        this.itemsContainer.addEventListener("input", (e) => {
          if (!e.target.classList.contains("cart-drawer__qty-input")) return;
          clearTimeout(qtyDebounce);
          qtyDebounce = setTimeout(() => {
            var key = e.target.dataset.key;
            var newQty = parseInt(e.target.value, 10);
            if (isNaN(newQty) || newQty < 1) newQty = 1;
            if (newQty > 99) newQty = 99;
            e.target.value = newQty;
            this.updateQuantity(key, newQty);
          }, CONFIG.DEBOUNCE_DELAY);
        });
      }

      // Interceptar formularios add-to-cart
      document.addEventListener("submit", (e) => {
        var form = e.target.closest(
          'form[action="/cart/add"], form[action*="/cart/add"]',
        );
        if (form) {
          e.preventDefault();
          this.handleAddToCart(form);
        }
      });

      // Focus trap dentro del drawer
      this.drawer.addEventListener("keydown", (e) => {
        if (e.key === "Tab") {
          this.handleTabKey(e);
        }
      });
    }

    /* ===== VISIBILIDAD PARA TECNOLOGÍA ASISTIVA =====
       Con el drawer cerrado el CSS solo lo desplazaba con translateX, así que
       sus botones y enlaces seguían siendo tabulables y el diálogo seguía
       anunciándose. `inert` (con aria-hidden de respaldo donde no existe) lo
       saca del orden de tabulación y del árbol de accesibilidad. */
    setInert(inert) {
      if ("inert" in HTMLElement.prototype) {
        this.drawer.inert = inert;
      }
      if (inert) {
        this.drawer.setAttribute("aria-hidden", "true");
        this.drawer.removeAttribute("aria-modal");
      } else {
        this.drawer.removeAttribute("aria-hidden");
        this.drawer.setAttribute("aria-modal", "true");
      }
    }

    /* ===== ABRIR DRAWER ===== */
    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.lastFocusedElement = document.activeElement;

      this.setInert(false);
      this.drawer.classList.add("active");
      this.backdrop.classList.add("active");
      document.body.classList.add("cart-drawer-open");

      // Focus en el botón cerrar
      setTimeout(() => {
        if (this.closeBtn) this.closeBtn.focus();
      }, 100);

      // Refrescar datos del carrito al abrir
      this.refreshCart(false);
    }

    /* ===== CERRAR DRAWER ===== */
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;

      // Devolver el foco antes de marcar inert: si el foco sigue dentro de un
      // subárbol inert el navegador lo manda al <body> y se pierde el sitio.
      if (this.lastFocusedElement && this.lastFocusedElement.isConnected) {
        this.lastFocusedElement.focus();
      }

      this.setInert(true);
      this.drawer.classList.remove("active");
      this.backdrop.classList.remove("active");
      document.body.classList.remove("cart-drawer-open");
    }

    /* ===== FOCUS TRAP ===== */
    handleTabKey(e) {
      var focusable = Array.prototype.filter.call(
        this.drawer.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
        // El estado vacío y el footer se ocultan con `hidden`, y sus controles
        // no se pueden enfocar. Sin este filtro el trap intentaba enfocar un
        // elemento invisible y el foco se escapaba del diálogo en silencio.
        function (el) {
          return !el.disabled && el.getClientRects().length > 0;
        },
      );

      if (focusable.length === 0) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          last.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    }

    /* ===== AÑADIR AL CARRITO (AJAX) ===== */
    async handleAddToCart(form) {
      if (this.isUpdating) return;
      this.isUpdating = true;

      var formData = new FormData(form);
      var submitBtn = form.querySelector('[type="submit"]');

      // Estado de loading en el botón
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.dataset.originalText = submitBtn.textContent;
        submitBtn.textContent = t("adding");
      }

      try {
        await utils.addToCartFromForm(formData);
        utils.announce(t("statusAdded"));
        await this.refreshCart(CONFIG.AUTO_OPEN_ON_ADD);
      } catch (error) {
        console.error("Error adding to cart:", error);
        this.showToast(error.message || t("errorAdd"), "error");
        utils.announce(error.message || t("errorAdd"));
      } finally {
        this.isUpdating = false;
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent =
            submitBtn.dataset.originalText || t("addToCart");
        }
      }
    }

    /* ===== ELIMINAR ITEM ===== */
    async removeItem(lineKey) {
      if (this.isUpdating) return;
      this.isUpdating = true;

      // Animación de salida optimista
      var itemEl = this.itemsContainer.querySelector(`[data-key="${lineKey}"]`);
      if (itemEl) {
        itemEl.classList.add("removing");
      }

      try {
        await utils.changeCart(lineKey, 0);
        // Esperar la animación y luego refrescar
        await this.delay(300);
        await this.refreshCart(false);
        utils.announce(t("statusRemoved"));
      } catch (error) {
        console.error("Error removing item:", error);
        // Revertir animación si falla
        if (itemEl) itemEl.classList.remove("removing");
        this.showToast(t("errorRemove"), "error");
        utils.announce(t("errorRemove"));
      } finally {
        this.isUpdating = false;
      }
    }

    /* ===== ACTUALIZAR CANTIDAD ===== */
    async updateQuantity(lineKey, quantity) {
      if (this.isUpdating) return;
      this.isUpdating = true;

      try {
        await utils.changeCart(lineKey, quantity);
        await this.refreshCart(false);
        utils.announce(t("statusUpdated"));
      } catch (error) {
        console.error("Error updating quantity:", error);
        this.showToast(t("errorUpdate"), "error");
        utils.announce(t("errorUpdate"));
      } finally {
        this.isUpdating = false;
      }
    }

    /* ===== REFRESCAR CARRITO COMPLETO ===== */
    async refreshCart(openDrawer) {
      var cart = await utils.fetchCart();
      if (!cart) return;

      this.cart = cart;

      // Actualizar toda la UI
      this.renderItems();
      this.updateSubtotal();
      this.updateShippingBar();
      utils.updateCartCount(cart.item_count);
      this.toggleEmptyState();

      // Abrir drawer si se indica
      if (openDrawer) this.open();
    }

    /* ===== RENDERIZAR ITEMS ===== */
    renderItems() {
      if (!this.itemsContainer || !this.cart) return;

      if (this.cart.items.length === 0) {
        this.itemsContainer.innerHTML = "";
        return;
      }

      var esc = utils.escapeHtml;

      this.itemsContainer.innerHTML = this.cart.items
        .map((item, index) => {
          var rawTitle = item.product_title || item.title;
          var title =
            rawTitle === "AERO2 Strips"
              ? `<img src="${esc(t("logoUrl"))}" alt="${esc(t("logoAlt"))}" class="cart-drawer__item-logo" width="62" height="15"><span class="cart-drawer__item-wordmark">Strips</span>`
              : this.highlightKeyword(rawTitle);
          var imageUrl = item.image ? utils.getSizedImageUrl(item.image, 240) : "";
          var variantTitle = item.variant_title || "";
          var linePrice = utils.formatMoney(item.line_price);
          var unitPrice = utils.formatMoney(item.price);
          var showUnit = item.quantity > 1;
          var removeLabel = esc(t("removeItem", { "%%TITLE%%": rawTitle }));

          return `
          <div class="cart-drawer__item" data-key="${esc(item.key)}" data-line="${index + 1}">
            <a href="${esc(item.url)}" class="cart-drawer__item-image-link" tabindex="-1" aria-hidden="true">
              ${
                imageUrl
                  ? `<img src="${esc(imageUrl)}" alt="" class="cart-drawer__item-image" width="90" height="90" loading="lazy">`
                  : `<div class="cart-drawer__item-image cart-drawer__item-image--placeholder"></div>`
              }
            </a>
            <div class="cart-drawer__item-info">
              <a href="${esc(item.url)}" class="cart-drawer__item-title">${title}</a>
              ${
                variantTitle
                  ? `<div class="cart-drawer__item-variant">${esc(t("size"))}<span class="cart-drawer__variant-value">${esc(variantTitle)}</span></div>`
                  : ""
              }
              <div class="cart-drawer__item-price">
                <span class="cart-drawer__item-line-price">${linePrice}</span>
                ${showUnit ? `<span class="cart-drawer__item-unit-price">${unitPrice} ${esc(t("each"))}</span>` : ""}
              </div>
              <div class="cart-drawer__qty-controls">
                <button type="button" class="cart-drawer__qty-btn" data-action="decrease" data-key="${esc(item.key)}" data-qty="${item.quantity}" aria-label="${esc(t("decrease"))}">−</button>
                <input type="number" class="cart-drawer__qty-input" value="${item.quantity}" min="1" max="99" data-key="${esc(item.key)}" aria-label="${esc(t("quantity"))}">
                <button type="button" class="cart-drawer__qty-btn" data-action="increase" data-key="${esc(item.key)}" data-qty="${item.quantity}" aria-label="${esc(t("increase"))}">+</button>
              </div>
            </div>
            <button type="button" class="cart-drawer__item-remove" data-key="${esc(item.key)}" aria-label="${removeLabel}">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        `;
        })
        .join("");
    }

    /* ===== RESALTAR PALABRA CLAVE EN VERDE =====
       Se parte el texto crudo y se escapa cada trozo por separado. Aplicar la
       expresión regular sobre HTML ya escapado podía cortar dentro de una
       entidad (&amp;) y romper el marcado. */
    highlightKeyword(text) {
      var esc = utils.escapeHtml;
      if (!CONFIG.HIGHLIGHT_KEYWORD || !text) return esc(text);

      var regex = new RegExp("(" + CONFIG.HIGHLIGHT_KEYWORD + "\\S*)", "gi");
      return String(text)
        .split(regex)
        .map(function (part, i) {
          // split() con un grupo de captura intercala las coincidencias en
          // las posiciones impares.
          return i % 2 === 1
            ? '<span class="highlight-keyword">' + esc(part) + "</span>"
            : esc(part);
        })
        .join("");
    }

    /* ===== ACTUALIZAR SUBTOTAL ===== */
    updateSubtotal() {
      if (!this.subtotalEl || !this.cart) return;
      this.subtotalEl.textContent = utils.formatMoney(this.cart.total_price);
    }

    /* ===== BARRA DE ENVÍO GRATIS ===== */
    updateShippingBar() {
      if (!this.shippingBar || !this.cart) return;

      if (!CONFIG.SHOW_SHIPPING_BAR) {
        this.shippingBar.hidden = true;
        return;
      }

      this.shippingBar.hidden = false;

      var info = utils.getShippingInfo(this.cart.total_price);

      if (this.shippingProgress) {
        this.shippingProgress.style.width = `${info.percentage}%`;
      }

      if (!this.shippingMsg) return;

      if (info.isFree) {
        this.shippingMsg.innerHTML = `
          <span>${utils.escapeHtml(t("shippingUnlocked"))}</span>
          <svg class="cart-drawer__shipping-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="1" y="3" width="15" height="13"></rect>
            <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
            <circle cx="5.5" cy="18.5" r="2.5"></circle>
            <circle cx="18.5" cy="18.5" r="2.5"></circle>
          </svg>
        `;
      } else {
        this.shippingMsg.textContent = t("shippingRemaining", {
          "%%AMOUNT%%": utils.formatMoney(info.remaining * 100),
        });
      }
    }

    /* ===== ALTERNAR ESTADO VACÍO / CON PRODUCTOS ===== */
    toggleEmptyState() {
      if (!this.cart) return;
      var hasItems = this.cart.item_count > 0;

      if (this.itemsContainer) this.itemsContainer.hidden = !hasItems;
      if (this.emptyState) this.emptyState.hidden = hasItems;
      if (this.footer) this.footer.hidden = !hasItems;
    }

    /* ===== TOAST NOTIFICATIONS ===== */
    showToast(message, type) {
      // Eliminar toast anterior si existe
      var existingToast = document.querySelector(".cart-drawer-toast");
      if (existingToast) existingToast.remove();

      var toast = document.createElement("div");
      toast.className = `cart-drawer-toast ${type === "error" ? "error" : ""}`;
      toast.textContent = message;
      document.body.appendChild(toast);

      // Activar transición
      requestAnimationFrame(() => {
        toast.classList.add("active");
      });

      // Auto-ocultar después de 3 segundos
      setTimeout(() => {
        toast.classList.remove("active");
        setTimeout(() => toast.remove(), 300);
      }, 3000);
    }

    /* ===== UTILIDADES ===== */
    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }
  }

  /* ---------- INICIALIZAR CUANDO EL DOM ESTÉ LISTO ---------- */
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => new CartDrawer());
  } else {
    new CartDrawer();
  }
})();
